/**
 * Story 7.18 (ID-1) — ONE predicate for "this horse is no longer available".
 *
 * A withdrawn horse (natively `closed`, not `sold`) gets the unavailable page
 * (S3): no photo, no name, nothing from the listing. The same predicate also
 * switches Download Ad off for everyone, the seller included (D9), so the page
 * and the `/api/download-ad` proxy can never disagree about which listings are
 * gone.
 *
 * The rulings this encodes (operator, 2026-10-01):
 *   - D1: native `closed` ONLY. `listingStatus: 'cancelled'` on a PUBLISHED
 *     listing does not trigger the page — a hand-reopened listing gets its
 *     full page back (S4).
 *   - D2: a natively closed listing still stamped `on_hold` is unavailable;
 *     native closed wins.
 *   - S1: a sold listing keeps its showcase page with the Sold medallion, so
 *     `closed` + `sold` is NOT unavailable.
 *   - S2: `published` + `on_hold` keeps its page and keeps taking inquiries.
 *   - D5: the owner never sees the unavailable view once their client has
 *     fetched the own listing — `isUnavailableForViewer` carries that arm.
 *
 * Authored as CommonJS so the SAME function is loaded by the webpack client
 * bundle (`import`) and the plain-Node server (`require`) — `yarn start` runs
 * `node server/index.js` directly and cannot load ESM from `src/`. Precedent:
 * `src/config/onboarding.js`, consumed by `server/api-util/tphOnboardingProxy.js`.
 *
 * Both constants are pinned LITERALLY in `listingAvailability.test.js`
 * (memory: iterating an allowlist is blind to removal).
 */

// The Sharetribe native listing state that means "the seller took it down".
const UNAVAILABLE_NATIVE_STATE = 'closed';

// The one `publicData.listingStatus` that keeps a closed listing's full page.
const SHOWCASE_LISTING_STATUS = 'sold';

/**
 * Is this listing unavailable, regardless of who is looking?
 *
 * Returns `true` ONLY when `state === 'closed'` AND `listingStatus !== 'sold'`.
 * Nothing else returns `true`: `published` (any status), `draft`,
 * `pendingApproval`, an unknown state, or no input at all are all `false`.
 *
 * @param {Object} [listing]
 * @param {string} [listing.state] listing.attributes.state
 * @param {string} [listing.listingStatus] listing.attributes.publicData.listingStatus
 * @returns {boolean}
 */
const isListingUnavailable = ({ state, listingStatus } = {}) =>
  state === UNAVAILABLE_NATIVE_STATE && listingStatus !== SHOWCASE_LISTING_STATUS;

/**
 * Is this listing unavailable TO THIS VIEWER? The owner arm (D5): the seller
 * who withdrew the horse still gets their full page.
 *
 * @param {Object} [listing]
 * @param {string} [listing.state]
 * @param {string} [listing.listingStatus]
 * @param {boolean} [listing.isOwn] the viewer owns the listing
 * @returns {boolean}
 */
const isUnavailableForViewer = ({ state, listingStatus, isOwn } = {}) =>
  !isOwn && isListingUnavailable({ state, listingStatus });

module.exports = {
  UNAVAILABLE_NATIVE_STATE,
  SHOWCASE_LISTING_STATUS,
  isListingUnavailable,
  isUnavailableForViewer,
};
